import React, { useEffect, useRef } from 'react';
import { useRecoilState } from 'recoil';
import { uiTheme } from 'states/UiState';
import { readUserTheme, systemTheme, themeForUser } from 'helpers/themePreference';
import { useThemeOwner } from 'states/useThemePreference';

/**
 * Component mounted once inside RecoilRoot; keeps <html data-theme> in step with the
 * uiTheme atom, loads the signed-in user's saved theme when someone signs in, and
 * follows OS preference changes for a user who has not chosen a theme yet.
 */
const ThemeInitializer: React.FC = () => {
  const [theme, setTheme] = useRecoilState(uiTheme);
  const owner = useThemeOwner();
  const loadedFor = useRef<string>();

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
  }, [theme]);

  // A different person signed in: show their theme, not the previous user's.
  useEffect(() => {
    if (!owner) {
      loadedFor.current = undefined;
      return;
    }
    if (loadedFor.current === owner) return;
    loadedFor.current = owner;
    setTheme(themeForUser(owner));
  }, [owner, setTheme]);

  // listen for OS preference changes if the user hasn't chosen a theme
  useEffect(() => {
    const mql = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = () => {
      if (!owner || !readUserTheme(owner)) setTheme(systemTheme());
    };
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, [owner, setTheme]);

  return null;
};

export default ThemeInitializer;
